import { Box, FormControlLabel, Grid, Stack } from "@mui/material";
import { useGetInstrumentDDMutation, useGetVariableBreakdownQuery } from "../../../../api/dashboardApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { setNewNotification } from "../../../common/notificationSlice";
import { selectSelectedDatabase } from "../../../common/databaseSlice";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import LassoSelect from "../../../../components/LassoSelect";
import { useWizardQuery } from "../../../../api/dataWranglerApiSlice";
import VariableDashletGrid from "./VariableDashletGrid";
import FieldToolbar from "./FieldToolbar";
import LassoSwitch from "../../../../components/form/LassoSwitch";
import { LassoTextField } from "../../../../components/LassoTextField";

const VariableBreakdown = () => {
    const { setHeaderText, setPreviousLocation, view } = useOutletContext<any>();
    const [searchParams, setSearchParams] = useSearchParams();
    const database = useSelector(selectSelectedDatabase);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const pathname = location.pathname.replace(/\/\s*$/, '');
    const paths = pathname.split('/');
    // Since instrument is extracted from the url, url parameters have to be decoded.
    const instrument = paths.length > 1 ? decodeURIComponent(paths[paths.length - 1]) : '';
    const [viewField, setViewField] = useState<string>('');
    const [fieldFilter, setFieldFilter] = useState<string>('');
    const [selectedCentres, setSelectedCentres] = useState<string[]>([]);
    const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
    const [breakdownView, setBreakdownView] = useState<"sites" | "visits">("sites");
    const [alignAxis, setAlignAxis] = useState<boolean>(false);
    const { data, isLoading, isFetching, isError } = useGetVariableBreakdownQuery({ database: database, instrument: instrument });
    const [triggerFetch, { isLoading: isUpdating, data: dataDictionary, isUninitialized }] = useGetInstrumentDDMutation();
    const { data: wizardConfig, isFetching: configFetching } = useWizardQuery({ database: database });

    useEffect(() => {
        setHeaderText(instrument);
        const previousLocation = location.state?.previousLocation ?? { pathname: '/by-domain' };
        setPreviousLocation(previousLocation);
        setSelectedSessions(searchParams.get("session")?.split(",") ?? []);
        setSelectedCentres(searchParams.get("centers")?.split(",") ?? []);
        setFieldFilter(searchParams.get("variable") ?? "");
    }, []);

    // The data dictionary belongs to a study, so it has to be re-fetched whenever the study changes.
    useEffect(() => {
        triggerFetch({ database: database, instrument: instrument });
    }, [database, instrument]);

    useEffect(() => {
        if (isError) {
            navigate('/by-domain');
        }
    }, [isError]);

    // The instrument may not exist in the newly selected study: drop the stale selection and
    // dictionary, reload them for the new database, and leave the page if there is nothing to show.
    const previousDatabase = useRef(database);
    useEffect(() => {
        if (previousDatabase.current === database) {
            return;
        }
        previousDatabase.current = database;
        setViewField('');
        setFieldFilter('');
        triggerFetch({ database: database, instrument: instrument });
    }, [database]);

    useEffect(() => {
        if (!isLoading && !isFetching && !isError && Array.isArray(data) && data.length === 0) {
            dispatch(setNewNotification({
                message: `${instrument} is not available in the selected dataset`,
                type: 'warning'
            }));
            navigate('/by-domain');
        }
    }, [data, isLoading, isFetching, isError]);

    const chartData = useMemo(() => {
        if (isLoading || isFetching || isUpdating || isUninitialized || !data || !dataDictionary) {
            return {};
        }

        const mapped: any = {};
        data.forEach((field: any) => {
            const agg: any = {};
            const sites: any = {};
            const visits: any = {};
            field.data.forEach((row: any) => {
                if (!agg[row.value]) {
                    agg[row.value] = {
                        count: 0,
                        visits: {},
                        sites: {},
                        isShadowValue: row.isShadowValue
                    };
                }

                if ((selectedSessions.length === 0 || selectedSessions.includes(row.visit)) &&
                    (selectedCentres.length === 0 || selectedCentres.includes(row.site))
                ) {
                    agg[row.value].count += row.count;
                    if (!sites[row.site]) {
                        sites[row.site] = {};
                    }
                    if (!sites[row.site][row.value]) {
                        sites[row.site][row.value] = 0;
                    }
                    sites[row.site][row.value] += row.count;
                    if (!visits[row.visit]) {
                        visits[row.visit] = {};
                    }
                    if (!visits[row.visit][row.value]) {
                        visits[row.visit][row.value] = 0;
                    }
                    visits[row.visit][row.value] += row.count;
                }
            });

            mapped[field.variable] = {
                label: field.variable,
                data: [],
                sites: {},
                visits: {}
            }
            Object.keys(agg).forEach((variable) => {
                mapped[field.variable].data.push({
                    levelValue: variable,
                    value: agg[variable].count,
                    isShadowValue: agg[variable].isShadowValue,
                    label: variable !== '' && Object.keys(dataDictionary[field.variable]?.levels || []).length > 0 && dataDictionary[field.variable]?.levels[variable]
                        ? dataDictionary[field.variable]?.levels[variable].label
                        : variable
                })
                Object.keys(sites).forEach((site) => {
                    if (!mapped[field.variable].sites[site]) {
                        mapped[field.variable].sites[site] = {
                            data: []
                        };
                    }
                    mapped[field.variable].sites[site].data.push({
                        label: variable !== '' && Object.keys(dataDictionary[field.variable]?.levels || []).length > 0 && dataDictionary[field.variable]?.levels[variable]
                            ? dataDictionary[field.variable]?.levels[variable].label
                            : variable,
                        levelValue: variable,
                        isShadowValue: agg[variable].isShadowValue,
                        value: sites[site][variable] ?? 0
                    })
                })
                Object.keys(visits).forEach((visit) => {
                    if (!mapped[field.variable].visits[visit]) {
                        mapped[field.variable].visits[visit] = {
                            data: []
                        };
                    }
                    mapped[field.variable].visits[visit].data.push({
                        label: variable !== '' && Object.keys(dataDictionary[field.variable]?.levels || []).length > 0 && dataDictionary[field.variable]?.levels[variable]
                            ? dataDictionary[field.variable]?.levels[variable].label
                            : variable,
                        levelValue: variable,
                        isShadowValue: agg[variable].isShadowValue,
                        value: visits[visit][variable] ?? 0
                    })
                })
            })
        })
        return mapped;
    }, [data, dataDictionary, selectedCentres, selectedSessions]);

    // Memoised so the dashlet grid keeps the same object between renders - rebuilding it on
    // every render invalidated the virtualiser's derived data on unrelated state changes.
    const filteredKeys = useMemo(() => {
        return Object.keys(chartData).filter((key) => {
            return key.toLowerCase().includes(fieldFilter.toLowerCase())
                || dataDictionary?.[key]?.label?.toLowerCase().includes(fieldFilter.toLowerCase())
        });
    }, [chartData, dataDictionary, fieldFilter]);

    const filteredChartData = useMemo(() => {
        if (fieldFilter === '') {
            return chartData;
        }

        const filtered: any = {};
        filteredKeys.forEach((key) => {
            filtered[key] = chartData[key];
        })

        return filtered;
    }, [chartData, filteredKeys, fieldFilter]);

    const dashletData = useMemo(() => {
        if (viewField === '') {
            return filteredChartData;
        }
        return chartData[viewField]?.[breakdownView] ?? {};
    }, [chartData, viewField, breakdownView, filteredChartData]);

    const isStaticInstrument = useMemo(() => {
        return Object.values(dataDictionary ?? {}).some((field: any) => field?.staticInstrument);
    }, [dataDictionary]);

    if (isLoading || isFetching || configFetching) {
        return <div>loading...</div>
    }

    const handleCentresChange = (event: any) => {
        const {
            target: { value },
        } = event;
        const val = typeof value === 'string' ? value.split(',') : value;
        setSearchParams((state) => {
            if (val.length === 0) {
                state.delete("centers");
            } else {
                state.set("centers", val);
            }
            return state;
        });
        setSelectedCentres(val);
    };

    const handleSessionChange = (event: any) => {
        const {
            target: { value },
        } = event;
        const val = typeof value === 'string' ? value.split(',') : value;
        setSearchParams((state) => {
            if (val.length === 0) {
                state.delete("session");
            } else {
                state.set("session", val);
            }
            return state;
        });
        setSelectedSessions(val);
    };

    const traverseVariables = (direction:  "left" | "right") => {
        if (filteredKeys.length <= 1) {
            return;
        }

        const current = filteredKeys.indexOf(viewField);
        const next = direction === 'right'
            ? (current + 1) % (filteredKeys.length - 1)
            : current === 0
                ? filteredKeys.length - 1
                : current - 1;

        setViewField(filteredKeys[next]);
    }

    return (
        <Stack width={"100%"} height={"100%"} paddingTop="32px" paddingBottom={2} gap={3}>
            <Stack flexDirection={"row"} width={"100%"} gap={3} alignItems={"center"}>
                <LassoTextField
                    disabled={viewField !== ''}
                    sx={{ flexGrow: 2, minWidth: 0 }}
                    value={fieldFilter}
                    onChange={(e) => {
                        setFieldFilter(e.target.value);
                        setSearchParams((state) => {
                            state.set("variable", e.target.value);
                            return state;
                        });
                    }}
                    size="small"
                    label={"Search Variable"}
                />
                <Box minHeight={0} minWidth={0} flexGrow={1}>
                    <LassoSelect
                        id={"variable-breakdown-site-select"}
                        label={'Filter by Sites'}
                        options={wizardConfig.centers.map((center: any) => {
                            return {
                                value: center.name,
                                label: center.name
                            }
                        })}
                        multiple={true}
                        value={selectedCentres}
                        onChange={handleCentresChange}
                        fullWidth={true}
                    />
                </Box>
                <Box minWidth={0} flexGrow={1}>
                    <LassoSelect
                        id={"variable-breakdown-visit-select"}
                        label={'Filter by Sessions'}
                        options={wizardConfig.ageGroups.map((group: any) => {
                            return {
                                value: group.label,
                                label: group.label
                            }
                        })}
                        multiple={true}
                        value={selectedSessions}
                        onChange={handleSessionChange}
                        fullWidth={true}
                        disabled={isStaticInstrument}
                    />
                </Box>
                <FormControlLabel
                    control={(
                        <LassoSwitch
                            checked={alignAxis}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAlignAxis(event.target.checked)}
                        />
                    )}
                    label={'Align Y-Axis'}
                    sx={{
                        marginLeft: 'auto'
                    }}
                />
            </Stack>
            {viewField !== '' && (
                <FieldToolbar
                    back={() => setViewField('')}
                    traverseVariables={traverseVariables}
                    view={breakdownView}
                    description={dataDictionary?.[viewField]?.label}
                    field={viewField}
                    setView={setBreakdownView}
                    isStaticInstrument={isStaticInstrument}
                />
            )}
            <Stack flexDirection={"row"} gap={3} width="100%" height={"100%"}>
                <Grid container spacing={2} sx={{ height: '100%' }}>
                    <VariableDashletGrid
                        chartData={dashletData}
                        dataDictionary={dataDictionary}
                        alignAxis={alignAxis}
                        setViewField={viewField !== '' ? undefined : setViewField}
                        field={viewField !== '' ? viewField : undefined}
                        showAsTable={view === 'table'}
                        showIssueOptions={true}
                    />
                </Grid>
            </Stack>
        </Stack>
    )
}

export default VariableBreakdown;
